import React from 'react';
import { Award, Briefcase, GraduationCap, Trophy } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { usePortfolio } from '../context/PortfolioContext';

const Achievements = () => {
  const { achievements, leadership, loading } = usePortfolio();
  const [ref, isVisible] = useScrollAnimation(0.1);

  const categoryIcons = {
    Recognition: Award,
    Award: Trophy,
    Education: GraduationCap,
    Competition: Trophy
  };

  if (loading || !achievements.length) return null;

  return (
    <section id="achievements" className="py-24 md:py-32 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="max-w-3xl mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Achievements & Recognition
          </h2>
          <p className="text-lg text-gray-700">
            Recognition for innovation, leadership, and impact in technology and entrepreneurship.
          </p>
        </div>

        <Tabs defaultValue="achievements" className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 mb-12">
            <TabsTrigger value="achievements">Achievements</TabsTrigger>
            <TabsTrigger value="leadership">Leadership Roles</TabsTrigger>
          </TabsList>

          {/* Achievements Tab */}
          <TabsContent value="achievements" className="space-y-0">
            <div
              ref={ref}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {achievements.map((achievement, index) => {
                const Icon = categoryIcons[achievement.category] || Award;
                return (
                  <div
                    key={achievement.id}
                    className={`${
                      isVisible ? 'animate-scale-in' : 'animate-on-scroll'
                    }`}
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <Card className="border-gray-200 hover:shadow-xl transition-all duration-500 group overflow-hidden h-full transform hover:-translate-y-2">
                      <CardContent className="p-6">
                        <div className="mb-4 flex items-start justify-between">
                          <div className="w-16 h-16 flex items-center justify-center bg-gray-50 rounded-lg group-hover:bg-gray-100 transition-all">
                            {achievement.logo ? (
                              <img
                                src={achievement.logo}
                                alt={achievement.title}
                                className="w-12 h-12 object-contain"
                              />
                            ) : (
                              <Icon className="text-gray-900" size={24} />
                            )}
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {achievement.year}
                          </Badge>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {achievement.title}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {achievement.description}
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* Leadership Tab */}
          <TabsContent value="leadership" className="space-y-0">
            <div className="grid md:grid-cols-2 gap-6">
              {leadership.map((role, index) => (
                <div
                  key={role.id}
                  className={`${
                    isVisible ? 'animate-fade-in-up' : 'animate-on-scroll'
                  }`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <Card className="border-gray-200 hover:shadow-xl transition-all duration-500 group h-full transform hover:-translate-y-2">
                    <CardContent className="p-6">
                      <div className="mb-4">
                        <div className="w-16 h-16 flex items-center justify-center bg-gray-50 rounded-lg group-hover:bg-gray-100 transition-all">
                          {role.logo ? (
                            <img
                              src={role.logo}
                              alt={role.organization}
                              className="w-12 h-12 object-contain"
                            />
                          ) : (
                            <Briefcase className="text-gray-900" size={24} />
                          )}
                        </div>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {role.title}
                      </h3>
                      <p className="text-base text-gray-700 mb-3">
                        {role.organization}
                      </p>
                      <p className="text-sm text-gray-600">{role.duration}</p>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default Achievements;